import { useMemo } from 'react'

import { Box, Text, Tooltip, theme, useToggle } from '@factorialco/deprecated-design-system'

import formatCurrency from 'modules/core/lib/formatCurrency'
import i18n from 'modules/core/lib/i18n'

import {
  PaymentRequestStatus,
  type PaymentStatus,
  getPaymentRequestStatusColor,
  getPaymentRequestStatusLabel,
  paymentStatusSortOrder,
} from 'modules/compensations/lib/constants'

type ProgressSegment = {
  amount: number
  status: PaymentStatus
}

type SegmentedProgressBarProps = {
  segments: ProgressSegment[]
  total: number
}

const Segment = ({ percentage, color }: { percentage: number; color: string }) => {
  return (
    <Box
      height="full"
      borderRadius={{ all: 'abs100' }}
      style={{ width: `${percentage}%`, backgroundColor: color }}
    />
  )
}

const ColorDot = ({ color }: { color: string }) => {
  return (
    <Box
      width="s12"
      height="s12"
      borderRadius={{ all: 'abs100' }}
      style={{ backgroundColor: color }}
    />
  )
}

const getOrder = (status: PaymentStatus) => paymentStatusSortOrder.indexOf(status)

const SegmentedProgressBar = ({ segments, total }: SegmentedProgressBarProps) => {
  const [open, toggle] = useToggle(false)

  const sortedSegments = useMemo(() => {
    return [...segments].sort((a, b) => getOrder(a.status) - getOrder(b.status))
  }, [segments])

  const legendSegments = useMemo(
    () => [
      {
        color: getPaymentRequestStatusColor(PaymentRequestStatus.PAID),
        label: getPaymentRequestStatusLabel(PaymentRequestStatus.PAID),
      },
      {
        color: getPaymentRequestStatusColor(PaymentRequestStatus.IN_PROGRESS),
        label: getPaymentRequestStatusLabel(PaymentRequestStatus.IN_PROGRESS),
      },
      {
        color: getPaymentRequestStatusColor(PaymentRequestStatus.PENDING_TO_PAY),
        label: i18n.t('compensations.payments.statuses.pending_to_send'),
      },
    ],
    []
  )

  const tooltipSegments = useMemo(() => {
    return paymentStatusSortOrder.map((status) => {
      const existing = segments.find((segment) => segment.status === status)

      return {
        status,
        amountCents: existing?.amount || 0,
        color: getPaymentRequestStatusColor(status),
        label: getPaymentRequestStatusLabel(status),
      }
    })
  }, [segments])

  return (
    <Tooltip.ItemList
      attachment="top center"
      targetAttachment="bottom center"
      toggle={toggle}
      open={open}
      size="auto"
    >
      <Box flexDirection="column" gap="s8">
        <Box gap="s8">
          <Box width="full" height="s8" style={{ overflow: 'hidden' }} flexDirection="row" gap="s4">
            {sortedSegments.length === 0 && (
              <Segment percentage={100} color={theme.colors.grey200} />
            )}

            {sortedSegments.map((segment, index) => {
              const percentage = (segment.amount / total) * 100

              return (
                <Segment
                  key={index}
                  percentage={percentage}
                  color={getPaymentRequestStatusColor(segment.status)}
                />
              )
            })}
          </Box>

          <Box flexDirection="row" gap="s16">
            {legendSegments.map((segment) => (
              <Box key={segment.label} flexDirection="row" alignItems="center" gap="s6">
                <ColorDot color={segment.color} />

                <Text.Normal size="300" color="textPrimary">
                  {segment.label}
                </Text.Normal>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box flexDirection="column" gap="s4">
        {tooltipSegments.map((segment) => (
          <Box
            key={segment.status}
            flexDirection="row"
            alignItems="center"
            justifyContent="spaceBetween"
            gap="s16"
          >
            <Box flexDirection="row" alignItems="center" gap="s8">
              <ColorDot color={segment.color} />
              <Text.Normal size="300" color="grey000White" whiteSpace="nowrap">
                {segment.label}
              </Text.Normal>
            </Box>
            <Text.Normal size="300" color="grey000White" weight="semibold" whiteSpace="nowrap">
              {formatCurrency(segment.amountCents, undefined, null, undefined)}
            </Text.Normal>
          </Box>
        ))}
      </Box>
    </Tooltip.ItemList>
  )
}

export default SegmentedProgressBar
